import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { Menu, Sun, Moon, LogOut, User as UserIcon, ChevronDown, Search, Settings, Check, Globe } from "lucide-react";
import { useAuth } from "@/stores/authStore";
import { LANGUAGES } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { initials } from "@/utils/format";
import { NotificationBell } from "@/components/NotificationBell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export function Topbar({ onMenu, title }) {
  const { t, i18n } = useTranslation();
  const { ctx, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const user = ctx?.user || {};
  const isAdmin = !!ctx?.is_super_admin;
  const roleName = isAdmin ? "Super Admin" : ctx?.role?.name || t("shell.noRole");
  const workspace = isAdmin ? t("shell.platform") : ctx?.tenant?.name || t("shell.company");

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const search = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    const isBooking = /^ARQ-B|^[A-Z]+-B-/i.test(term);
    navigate(`/dashboard/${isBooking ? "bookings" : "workorders"}?q=${encodeURIComponent(term)}`);
    setQ("");
  };

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border/60 bg-background/80 px-4 backdrop-blur-xl lg:px-6">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenu} data-testid="topbar-menu-button" aria-label={t("shell.openMenu")}>
        <Menu className="h-5 w-5" />
      </Button>

      <nav aria-label={t("shell.breadcrumbs")} className="flex min-w-0 items-center gap-2 text-sm">
        <span className="hidden truncate text-muted-foreground md:inline">{workspace}</span>
        <span className="hidden text-muted-foreground/40 md:inline">/</span>
        <span data-testid="topbar-title" className="truncate font-display text-[15px] font-semibold tracking-tight text-foreground">{title}</span>
      </nav>

      {!isAdmin && (
        <form onSubmit={search} role="search" className="ml-auto hidden w-full max-w-sm md:block">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("shell.searchPlaceholder")} aria-label={t("shell.search")}
              data-testid="global-search-input"
              className="h-9 w-full rounded-lg border border-border/70 bg-card pl-9 pr-3 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary/50 focus:ring-2 focus:ring-primary/20" />
          </label>
        </form>
      )}

      <div className={`flex items-center gap-1 ${isAdmin ? "ml-auto" : "md:ml-0 ml-auto"}`}>
        {!isAdmin && ctx?.tenant && <NotificationBell />}
        <LanguageSwitcher variant="light" className="hidden md:inline-flex" />
        <Button variant="ghost" size="icon" className="relative rounded-lg text-muted-foreground hover:text-foreground"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle-button" aria-label={t("shell.toggleTheme")}>
          <Sun className="h-5 w-5 rotate-0 scale-100 transition-transform duration-300 dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-transform duration-300 dark:rotate-0 dark:scale-100" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger" aria-label={t("shell.userMenu")}
              className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 transition-colors duration-200 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-xs font-semibold text-primary-foreground">
                {initials(user.name) || <UserIcon className="h-4 w-4" />}
              </span>
              <span className="hidden text-left sm:block">
                <span className="block text-sm font-medium leading-tight">{user.name?.split(" ")[0]}</span>
                <span className="block text-[11px] text-muted-foreground">{roleName}</span>
              </span>
              <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 bg-popover" data-testid="user-menu-content">
            <DropdownMenuLabel>
              <p className="text-sm font-medium">{user.name}</p>
              <p className="text-xs font-normal text-muted-foreground">{user.email}</p>
              <p className="mt-1 text-[11px] font-normal text-muted-foreground">{roleName}{ctx?.tenant?.name ? ` · ${ctx.tenant.name}` : ""}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!isAdmin && (
              <>
                <DropdownMenuItem onClick={() => navigate("/dashboard/users")} data-testid="menu-profile"><UserIcon className="mr-2 h-4 w-4" /> {t("shell.profile")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard/settings")} data-testid="menu-settings"><Settings className="mr-2 h-4 w-4" /> {t("shell.settings")}</DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuLabel className="flex items-center gap-2 text-xs font-normal text-muted-foreground"><Globe className="h-3.5 w-3.5" /> {t("shell.language")}</DropdownMenuLabel>
            {LANGUAGES.map((l) => (
              <DropdownMenuItem key={l.code} onClick={() => i18n.changeLanguage(l.code)} data-testid={`menu-language-${l.code}`} className="flex items-center gap-2">
                <span>{l.flag}</span><span className="flex-1">{l.label}</span>
                {i18n.resolvedLanguage === l.code && <Check className="h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" /> {t("shell.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
